import React from 'react';

/**
 * The header above the block workspace, shared by the Explore tutorial and the achievement goals
 * so both read at the same size.
 *
 * The hierarchy is deliberately inverted from the usual title-first layout: the title names the
 * task and is read once, while the prompt is what a student acts on, so the prompt is the largest
 * and highest-contrast text and the title is demoted to a label above it.
 *
 * This replaced a measure-and-reflow layout that picked between four row/stack arrangements from
 * element widths. Centred column + max-width wraps correctly at any width without measuring, and
 * without the stale-measurement bug that came with it.
 */

/** Max line length for the prompt. Wide enough that long goal descriptions stay to ~2 lines. */
const PROMPT_MAX_WIDTH_PX = 820;

interface WorkspaceHeaderProps {
  icon: string;
  title: string;
  prompt: string;
  /**
   * Announce prompt changes to assistive tech. Set for the tutorial, whose prompt is replaced in
   * place as stages clear; goal descriptions change only through an explicit navigation.
   */
  announcePromptChanges?: boolean;
  /** Controls row beneath the prompt — buttons, progress dots. */
  children?: React.ReactNode;
}

export function WorkspaceHeader({
  icon,
  title,
  prompt,
  announcePromptChanges = false,
  children,
}: WorkspaceHeaderProps) {
  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
      }}
    >
      <div className="tw-flex tw-items-center" style={{ gap: 'var(--spacing-2)', flexShrink: 0 }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
        <h3 style={{ color: 'var(--muted-foreground)', margin: 0, fontSize: '18px' }}>{title}</h3>
      </div>

      <p
        role={announcePromptChanges ? 'status' : undefined}
        aria-live={announcePromptChanges ? 'polite' : undefined}
        style={{
          color: 'var(--foreground)',
          margin: 0,
          textAlign: 'center',
          maxWidth: `${PROMPT_MAX_WIDTH_PX}px`,
          fontSize: '24px',
          lineHeight: 1.35,
        }}
      >
        {prompt}
      </p>

      {children && (
        <div
          className="tw-flex tw-flex-wrap tw-items-center tw-justify-center"
          style={{ gap: 'var(--spacing-3)', marginTop: 'var(--spacing-1, 4px)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
